// The second nav panel: one module's own menu, attached to the right of the
// main sidebar.
//
// Why this exists. Org Attendance is eleven screens. Unfolded into the main
// nav they push everything below them off the fold, and a sidebar you have to
// scroll to find Settings in is a sidebar nobody reads. So the module gets one
// row in the main nav, and its eleven screens live here instead, where there
// is room to group them and label the groups.
//
// While this is up the main nav shrinks to its icon rail rather than
// disappearing: you can still see where you are in the product, and one click
// on Back brings the full menu straight back without leaving the page you are
// on.
//
// Desktop only. On a phone the main nav is already a full-screen drawer and a
// second panel beside it has nowhere to go, so the module keeps unfolding
// inline there.

import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';
// @mui
import { Box, Divider, IconButton, List, Stack, Tooltip, Typography } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// guards
import { isItemVisible } from '../../../guards/checkNavAccess';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import NavItem from '../../../components/nav-section/vertical/NavItem';
import { getActive } from '../../../components/nav-section';
import { ListSubheaderStyle } from '../../../components/nav-section/vertical/style';

// ----------------------------------------------------------------------

NavbarFlyout.propTypes = {
  item: PropTypes.object.isRequired,
  onBack: PropTypes.func,
};

export default function NavbarFlyout({ item, onBack }) {
  const { pathname } = useLocation();
  const { user, hasPermission } = useAuth();

  // Group the children by their `group` label, keeping the order they are
  // written in. An item with no group falls into an unlabelled first block, so
  // a module that has not been grouped yet still renders as a plain list.
  const groups = useMemo(() => {
    const visible = (item.children || []).filter((child) =>
      isItemVisible(child, { user, hasPermission })
    );
    const order = [];
    const byLabel = {};
    visible.forEach((child) => {
      const label = child.group || '';
      if (!byLabel[label]) {
        byLabel[label] = [];
        order.push(label);
      }
      byLabel[label].push(child);
    });
    return order.map((label) => ({ label, items: byLabel[label] }));
  }, [item.children, user, hasPermission]);

  return (
    <Scrollbar sx={{ height: 1, '& .simplebar-content': { height: 1 } }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ pl: 0.5, pr: 1, pt: 2, pb: 1.5, minHeight: 80 }}
      >
        <Tooltip title="Back to the main menu" placement="right">
          <IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary' }}>
            <Iconify icon="eva:arrow-ios-back-fill" sx={{ width: 20, height: 20 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {item.title}
          </Typography>
          <Typography variant="caption" color="text.disabled" noWrap>
            Your own team
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mx: 2, mb: 1 }} />

      {groups.map((group) => (
        <List key={group.label || 'ungrouped'} disablePadding sx={{ pl: 0.75, pr: 1 }}>
          {group.label && (
            <ListSubheaderStyle sx={{ pt: 1.5, pb: 0.5 }}>{group.label}</ListSubheaderStyle>
          )}
          {group.items.map((child) => (
            <NavItem
              key={child.title + child.path}
              item={child}
              depth={1}
              // `exact` is for a row whose path is the module root: without it
              // getActive matches every screen underneath and the overview row
              // would look current no matter where you actually are.
              active={
                child.exact
                  ? !!matchPath({ path: child.path, end: true }, pathname)
                  : getActive(child.path, pathname)
              }
              component={RouterLink}
              to={child.path}
            />
          ))}
        </List>
      ))}
    </Scrollbar>
  );
}
